import { useMemo, useState } from 'react';
import axios from 'axios';
import {
	API_ENDPOINTS,
	Badge,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	Dropdown,
	FormInput,
	Icons,
	Modal,
	Permissions,
	replaceKeyWithValue,
	route,
	Sizes,
	StringHelpers,
	SubTitle,
	useAuthorisationContext,
	useDisabledContext,
	useLangContext,
	Variants,
} from 'carrier-fe';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import {
	ApplianceAttributes,
	ApplianceWarranty,
	WarrantyOptionChoice,
} from '../../../types/appliance-management';
import ImportWarrantyModal from './ImportWarrantyModal';

type Props = {
	applianceId: string;
	applianceName: string;
	warranties: ApplianceWarranty[];
	attributes: ApplianceAttributes | null;
	/** Sorted list of lengths a warranty certificate template exists for. Advisory only. */
	certificateTemplateLengths: number[];
	canEdit: boolean;
	onChanged: () => void;
};

/**
 * The order the two channel groups are shown in, matching the legacy warranty matrix's left-to-right
 * blocks ("Via VPlus" then "Non VPlus") and the enum's case order.
 *
 * Pinned here rather than taken from whichever source built the group list, so the blocks do not swap
 * position between the view page - which derives them from the options themselves - and the edit page,
 * which has the attributes endpoint to order them by.
 */
const CHANNEL_ORDER = ['vplus_portal', 'non_vplus_portal'];

const emptyForm = {
	/** The channel:tier pair, so the dropdown is CUS-935's single "Warranty Name" list. */
	warranty_option: '',
	length_years: '',
	price: '0',
	vipartner_price: '',
	vipartner_only: false,
	is_default: false,
};

type FormValues = typeof emptyForm;

/**
 * CUS-935's warranty assignment, and the CUS-936 copy entry point.
 *
 * Two departures from the ticket, both forced by the API and both noted on it.
 *
 * The ticket describes "+ Warranty" repeating the fields with one Submit at the end, but the API only has
 * per-option store, update and delete - no bulk replace. So each option saves on its own. That also means a
 * failure cannot leave the appliance half-saved, and every change is one audited request.
 *
 * And the ticket has a single "VI Partner" checkbox where the data needs three separate things: an
 * alternate VI Partner price (live today on Vitocal 150-A and 151-A, which the legacy sheet stored as
 * "£720 regular installer / £540 VIPartner"), a VI-Partner-only gate for CUS-940, and the automatic default
 * flag - which is not optional, because it is what the nightly auto-warranty job selects.
 */
function WarrantyOptionsSection({
	applianceId,
	applianceName,
	warranties,
	attributes,
	certificateTemplateLengths,
	canEdit,
	onChanged,
}: Props) {
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const [formOpen, setFormOpen] = useState<boolean>(false);
	const [editing, setEditing] = useState<ApplianceWarranty | null>(null);
	const [deleting, setDeleting] = useState<ApplianceWarranty | null>(null);
	const [importOpen, setImportOpen] = useState<boolean>(false);
	const [values, setValues] = useState<FormValues>(emptyForm);
	const [errors, setErrors] = useState<Record<string, string[]>>({});

	const canAdd =
		canEdit &&
		hasPermissionAccess(
			Permissions.VIESSMANN_ADMIN_APPLIANCE_WARRANTY_STORE
		);
	const canUpdate =
		canEdit &&
		hasPermissionAccess(
			Permissions.VIESSMANN_ADMIN_APPLIANCE_WARRANTY_UPDATE
		);
	const canDelete =
		canEdit &&
		hasPermissionAccess(
			Permissions.VIESSMANN_ADMIN_APPLIANCE_WARRANTY_DELETE
		);

	/*
	 * Memoised because `?? []` would hand a new array to the three useMemos below on every render, which
	 * would make all of them recompute every time.
	 */
	const allOptions = useMemo(
		() => attributes?.warranty_options ?? [],
		[attributes]
	);

	/**
	 * The channel groups to render.
	 *
	 * Built from the warranty options themselves first, because those are the only source available on the
	 * read-only page: it does not fetch the attributes endpoint, since that authorises viewAny and would
	 * 403 a user who legitimately has view rights alone. Deriving the groups from the attributes alone left
	 * this section rendering nothing at all on the view page, options loaded and never shown.
	 *
	 * The attributes are then unioned in when they are available, so the editable page also shows an empty
	 * group for a channel that has no options yet - otherwise there is nowhere to see that one is missing.
	 * The result is sorted into CHANNEL_ORDER so the two pages agree on which block comes first.
	 */
	const channels = useMemo(() => {
		const seen: { value: string; label: string }[] = [];

		const add = (value?: string | null, label?: string | null) => {
			if (!value || seen.some((item) => item.value === value)) return;

			seen.push({ value, label: label || value });
		};

		allOptions.forEach((option) => add(option.channel, option.channel_display));
		warranties.forEach((warranty) =>
			add(warranty.channel, warranty.channel_display)
		);

		// Unknown channels sort last rather than vanishing, so a new one is visible rather than silent.
		const rank = (value: string) => {
			const index = CHANNEL_ORDER.indexOf(value);

			return index === -1 ? CHANNEL_ORDER.length : index;
		};

		return seen.sort((a, b) => rank(a.value) - rank(b.value));
	}, [allOptions, warranties]);

	const selectedChoice: WarrantyOptionChoice | undefined = useMemo(
		() => allOptions.find((option) => option.value === values.warranty_option),
		[allOptions, values.warranty_option]
	);

	/*
	 * CUS-935's "each Warranty Name can only be added once" - also a database unique index on
	 * (appliance, channel, tier). Pairs already in use are dropped from the list, except the one being
	 * edited, which would otherwise vanish from its own form.
	 */
	const availableOptions = useMemo(() => {
		const used = warranties
			.filter((warranty) => warranty.id !== editing?.id)
			.map((warranty) => `${warranty.channel}:${warranty.tier}`);

		return allOptions
			.filter((option) => !used.includes(option.value))
			.map((option) => ({ label: option.label, value: option.value }));
	}, [allOptions, warranties, editing]);

	/*
	 * Mirrors WarrantyPriceMatchesTier so the user is not told after submitting. Only the two chargeable
	 * extensions may carry a price; everything else must be 0.
	 */
	const priceAllowed = selectedChoice?.is_chargeable ?? false;

	/*
	 * Mirrors WarrantyDefaultIsNotChargeable. The nightly job cannot pay for an option, cannot pick one no
	 * customer could choose, and must not pick one only VI Partners can see.
	 */
	const defaultAllowed =
		!!selectedChoice &&
		selectedChoice.is_selectable &&
		!selectedChoice.is_chargeable &&
		Number(values.price || 0) === 0 &&
		!values.vipartner_only;

	const lengthHasTemplate = (length: number | null) =>
		!length ||
		certificateTemplateLengths.length === 0 ||
		certificateTemplateLengths.includes(length);

	const openCreate = () => {
		setEditing(null);
		setValues(emptyForm);
		setErrors({});
		setFormOpen(true);
	};

	const openEdit = (warranty: ApplianceWarranty) => {
		setEditing(warranty);
		setValues({
			warranty_option: `${warranty.channel}:${warranty.tier}`,
			length_years: String(warranty.length_years ?? ''),
			price: String(warranty.price ?? 0),
			vipartner_price:
				warranty.vipartner_price === null
					? ''
					: String(warranty.vipartner_price),
			vipartner_only: warranty.vipartner_only,
			is_default: warranty.is_default,
		});
		setErrors({});
		setFormOpen(true);
	};

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
		setErrors({});
	};

	const save = async () => {
		if (!selectedChoice) return;

		setDisabled(true);
		setErrors({});

		const payload = {
			channel: selectedChoice.channel,
			tier: selectedChoice.tier,
			length_years: Number(values.length_years),
			price: priceAllowed ? Number(values.price || 0) : 0,
			/*
			 * Blank means "the same price for everyone", which the column represents as null. Sending 0
			 * instead would mean "free for VI Partners", a different thing entirely.
			 */
			vipartner_price:
				priceAllowed && values.vipartner_price !== ''
					? Number(values.vipartner_price)
					: null,
			vipartner_only: values.vipartner_only,
			is_default: defaultAllowed ? values.is_default : false,
		};

		try {
			const { data } = editing
				? await axios.patch(
						route(
							API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.WARRANTY
								.UPDATE,
							{
								viessmannAppliance: applianceId,
								warranty: editing.id,
							}
						),
						payload
				  )
				: await axios.post(
						route(
							API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.WARRANTY
								.STORE,
							{ viessmannAppliance: applianceId }
						),
						payload
				  );

			customToast({
				title: data?.message || 'Warranty option saved.',
				variant: Variants.Success,
			});

			onChanged();
			closeForm();
		} catch (err: any) {
			setErrors(err?.response?.data?.data?.errors ?? {});

			customToast({
				title:
					err?.response?.data?.message ||
					'Error saving the warranty option.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const remove = async () => {
		if (!deleting) return;

		setDisabled(true);

		try {
			const { data } = await axios.delete(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.WARRANTY.DELETE, {
					viessmannAppliance: applianceId,
					warranty: deleting.id,
				})
			);

			customToast({
				title: data?.message || 'Warranty option deleted.',
				variant: Variants.Success,
			});

			onChanged();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error deleting the warranty option.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			setDeleting(null);
		}
	};

	const rowActions = (warranty: ApplianceWarranty): DropdownItemProps[] => {
		const actions: DropdownItemProps[] = [];

		if (canUpdate) {
			actions.push({
				label: crud?.select_labels?.edit || 'Edit',
				icon: Icons.EDIT,
				onClick: () => openEdit(warranty),
			});
		}

		if (canDelete) {
			actions.push({
				label: crud?.select_labels?.delete || 'Delete',
				icon: Icons.DELETE,
				variant: Variants.Danger,
				onClick: () => setDeleting(warranty),
			});
		}

		return actions;
	};

	const money = (amount: number) => `£${amount.toLocaleString('en-GB')}`;

	return (
		<>
			<Card className="mb-4">
				<CardBody>
					<div className="d-flex justify-content-between align-items-start mb-3">
						<div>
							<SubTitle
								title={StringHelpers.title(
									crud?.sub_titles?.warranty_options ||
										'Warranty Options'
								)}
								style={{ color: '#464C5E' }}
							/>
							<div className="text-muted">
								{fields?.warranty_options_description ||
									'What a customer can be offered after registering this appliance, grouped by where they registered it.'}
							</div>
						</div>
						{canAdd && (
							<div className="d-flex gap-2">
								<Button
									label={StringHelpers.title(
										crud?.select_labels?.import_warranty ||
											'Import Warranty'
									)}
									onClick={() => setImportOpen(true)}
									variant={Variants.Dark}
									type={ButtonTypes.Outline}
									disabled={disabled}
								/>
								<Button
									label={StringHelpers.title(
										crud?.buttons?.create?.default ||
											'Create'
									)}
									onClick={openCreate}
									variant={Variants.Dark}
									type={ButtonTypes.Outline}
									disabled={
										disabled ||
										availableOptions.length === 0
									}
								/>
							</div>
						)}
					</div>

					{warranties.length === 0 && (
						<p>
							{fields?.no_warranty_options ||
								'This appliance has no warranty options, so nothing will be offered to a customer who registers it and the nightly job will not apply one.'}
						</p>
					)}

					{channels.map((channel) => {
						const forChannel = warranties.filter(
							(warranty) => warranty.channel === channel.value
						);

						if (forChannel.length === 0 && warranties.length === 0) {
							return null;
						}

						return (
							<div
								key={channel.value}
								className="mb-4"
							>
								<h6
									className="text-uppercase fw-bold"
									style={{
										color: '#1E82E6',
										fontSize: '14px',
									}}
								>
									{channel.label}
								</h6>

								{forChannel.length === 0 ? (
									<p className="text-muted mb-0">
										{fields?.no_warranty_options_for_channel ||
											'No warranty options for this portal.'}
									</p>
								) : (
									forChannel.map((warranty) => (
										<Card
											key={warranty.id}
											className="mb-2"
										>
											<CardBody className="d-flex justify-content-between align-items-start">
												<div>
													<div className="d-flex align-items-center gap-2 mb-1">
														<h6 className="mb-0">
															{warranty.name ||
																'-'}
														</h6>
														{/*
														 * What the nightly auto-warranty job applies when a
														 * customer never chooses. Replaces "whichever
														 * spreadsheet column happened to come first".
														 */}
														{warranty.is_default && (
															<Badge
																label={
																	fields?.is_default ||
																	'Automatic default'
																}
																variant={
																	Variants.Success
																}
															/>
														)}
														{warranty.vipartner_only && (
															<Badge
																label={
																	fields?.vipartner_only ||
																	'VI Partner only'
																}
																variant={
																	Variants.Warning
																}
															/>
														)}
														<Badge
															label={
																warranty.source_display ||
																'-'
															}
															variant={
																warranty.source ===
																'legacy_import'
																	? Variants.Light
																	: Variants.Dark
															}
														/>
													</div>
													<div className="text-muted">
														{`${
															warranty.length_years ??
															'-'
														} ${
															warranty.length_years ===
															1
																? 'year'
																: 'years'
														} · ${money(
															warranty.price ?? 0
														)}`}
														{warranty.vipartner_price !==
															null &&
															` · ${
																fields?.vipartner_price ||
																'VI Partner price'
															} ${money(
																warranty.vipartner_price
															)}`}
													</div>
													{/*
													 * Advisory, never blocking: production already holds
													 * 15-year Vitovolt warranties with no template, and a
													 * blocking rule would freeze rows an admin may need to
													 * correct.
													 */}
													{warranty.has_certificate_template ===
														false && (
														<div className="text-warning mt-1">
															{replaceKeyWithValue(
																fields?.no_certificate_template ||
																	'No warranty certificate template exists for :length years, so no certificate can be produced for this option.',
																'length',
																String(
																	warranty.length_years ??
																		''
																)
															)}
														</div>
													)}
												</div>
												{rowActions(warranty).length >
													0 && (
													<Dropdown
														label={StringHelpers.title(
															crud?.buttons
																?.actions
																?.default ||
																'Actions'
														)}
														items={rowActions(
															warranty
														)}
														type={
															ButtonTypes.Outline
														}
														variant={Variants.Dark}
														size={Sizes.Small}
													/>
												)}
											</CardBody>
										</Card>
									))
								)}
							</div>
						);
					})}
				</CardBody>
			</Card>

			<Modal
				open={formOpen}
				onClose={closeForm}
				title={StringHelpers.title(
					replaceKeyWithValue(
						editing
							? crud?.pages?.edit?.title || 'Edit :model'
							: crud?.pages?.create?.title || 'Create :model',
						'model',
						crud?.models?.viessmann_appliance_warranty ||
							'Warranty Option'
					)
				)}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				/*
				 * In-flight state only. The shared Modal passes this to the X and Cancel as well as to
				 * Confirm, so gating it on the required fields would leave the user unable to close a form
				 * they had not completed. The required-field check sits on the confirm button instead.
				 */
				disabled={disabled}
				onConfirm={save}
				overriddenConfirmButton={
					<Button
						label={
							disabled
								? crud?.buttons?.submit?.submitting ||
								  'Saving...'
								: crud?.buttons?.submit?.default || 'Save'
						}
						variant={Variants.Primary}
						onClick={save}
						disabled={
							disabled ||
							!values.warranty_option ||
							!values.length_years
						}
					/>
				}
				variant={Variants.Primary}
			>
				<FormInput
					type="select"
					name="warranty_option"
					label={StringHelpers.title(fields?.tier || 'warranty name')}
					placeholder={StringHelpers.title(
						fields?.please_select || 'please select'
					)}
					value={values.warranty_option}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							warranty_option: value as string,
						}))
					}
					options={availableOptions}
					disabled={disabled}
					errorMessages={errors?.tier ?? errors?.channel}
				/>

				{selectedChoice && !selectedChoice.is_selectable && (
					<p className="text-muted">
						{fields?.out_of_box_note ||
							'This warranty comes with the appliance and is never offered to a customer to choose. It is recorded here for completeness.'}
					</p>
				)}

				<FormInput
					type="number"
					name="length_years"
					label={`${StringHelpers.title(
						fields?.length_years || 'warranty length'
					)} (${fields?.years || 'years'})`}
					value={values.length_years}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							length_years: value as string,
						}))
					}
					disabled={disabled}
					errorMessages={errors?.length_years}
				/>

				{!lengthHasTemplate(Number(values.length_years) || null) && (
					<p className="text-warning">
						{replaceKeyWithValue(
							fields?.no_certificate_template_warning ||
								'There is no warranty certificate template for :length years. You can still save this, but no certificate will be produced.',
							'length',
							values.length_years
						)}
					</p>
				)}

				<FormInput
					type="number"
					name="price"
					label={`${StringHelpers.title(
						fields?.warranty_price || 'warranty price'
					)} (£)`}
					description={
						priceAllowed
							? undefined
							: fields?.price_must_be_free ||
							  'Only a chargeable extension can carry a price, so this stays at £0.'
					}
					value={priceAllowed ? values.price : '0'}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							price: value as string,
						}))
					}
					disabled={disabled || !priceAllowed}
					errorMessages={errors?.price}
				/>

				<FormInput
					type="number"
					name="vipartner_price"
					label={`${fields?.vipartner_price || 'VI Partner Price'} (£)`}
					description={
						fields?.vipartner_price_description ||
						'Leave blank to charge VI Partners the same as everyone else.'
					}
					value={priceAllowed ? values.vipartner_price : ''}
					onChange={(value) =>
						setValues((prev) => ({
							...prev,
							vipartner_price: value as string,
						}))
					}
					disabled={disabled || !priceAllowed}
					errorMessages={errors?.vipartner_price}
				/>

				<div className="mb-3 form-check">
					<input
						type="checkbox"
						id="vipartner_only"
						name="vipartner_only"
						className="form-check-input"
						checked={values.vipartner_only}
						disabled={disabled}
						onChange={(event) =>
							setValues((prev) => ({
								...prev,
								vipartner_only: event.target.checked,
								is_default: event.target.checked
									? false
									: prev.is_default,
							}))
						}
					/>
					<label
						className="form-check-label"
						htmlFor="vipartner_only"
					>
						{fields?.vipartner_only || 'VI Partner Only'}
					</label>
					<div className="text-muted">
						{fields?.vipartner_only_description ||
							'Hide this option from installers who are not VI Partners.'}
					</div>
					{!!errors?.vipartner_only && (
						<div className="invalid-feedback d-block">
							{errors.vipartner_only}
						</div>
					)}
				</div>

				<div className="mb-0 form-check">
					<input
						type="checkbox"
						id="is_default"
						name="is_default"
						className="form-check-input"
						checked={values.is_default && defaultAllowed}
						disabled={disabled || !defaultAllowed}
						onChange={(event) =>
							setValues((prev) => ({
								...prev,
								is_default: event.target.checked,
							}))
						}
					/>
					<label
						className="form-check-label"
						htmlFor="is_default"
					>
						{StringHelpers.title(
							fields?.is_default || 'automatic default'
						)}
					</label>
					<div className="text-muted">
						{defaultAllowed
							? fields?.is_default_description ||
							  'Apply this option automatically if the customer never chooses one. Only one option per portal can be the automatic default; setting it here moves it off whichever option holds it now.'
							: fields?.is_default_unavailable ||
							  'Only a free option that a customer could choose, and that is not VI Partner only, can be the automatic default.'}
					</div>
					{!!errors?.is_default && (
						<div className="invalid-feedback d-block">
							{errors.is_default}
						</div>
					)}
				</div>
			</Modal>

			<Modal
				open={!!deleting}
				onClose={() => setDeleting(null)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.delete?.title || 'Delete :name?',
						'name',
						crud?.models?.viessmann_appliance_warranty ||
							'Warranty Option'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.delete?.submitting || 'Deleting...'
						: crud?.buttons?.delete?.default || 'Delete'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={remove}
				disabled={disabled}
				variant={Variants.Danger}
			>
				<p>
					{replaceKeyWithValue(
						fields?.delete_warranty_option_confirm ||
							'Delete :name? It will no longer be offered for this appliance. Warranties already sold under it are unaffected, and the change is recorded in the audit log.',
						'name',
						deleting?.name || ''
					)}
				</p>
				{/*
				 * Losing the default silently is worse than losing the option: the nightly job stops applying
				 * anything to this appliance until another option is marked.
				 */}
				{deleting?.is_default && (
					<p className="fw-bold text-danger mb-0">
						{fields?.delete_default_warranty_warning ||
							'This is the automatic default. Until another option is marked as the default, nothing will be applied automatically for this portal.'}
					</p>
				)}
			</Modal>

			<ImportWarrantyModal
				open={importOpen}
				onClose={() => setImportOpen(false)}
				applianceId={applianceId}
				applianceName={applianceName}
				currentWarrantyCount={warranties.length}
				onCopied={onChanged}
			/>
		</>
	);
}

export default WarrantyOptionsSection;
